/**
 * Main authentication form class
 * Coordinates all handlers
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import AuthAPI from './api/AuthAPI';
import FormValidator from './utils/FormValidator';
import DOMUtils from './utils/DOMUtils';
import StepNavigator from './utils/StepNavigator';
import Messages from './utils/Messages';
import LoginHandler from './handlers/LoginHandler';
import RegisterHandler from './handlers/RegisterHandler';
import PasswordResetHandler from './handlers/PasswordResetHandler';
import ChangePasswordHandler from './handlers/ChangePasswordHandler';
import EmailVerificationHandler from './handlers/EmailVerificationHandler';
import SSOHandler from './handlers/SSOHandler';
import { FORM_SELECTORS, FIELD_SELECTORS } from './constants';
import DateInput from '../../../resources/scripts/components/c-date-input';

class AuthForm {
  constructor(element) {
    this.form = element;
    this.api = new AuthAPI();
    this.stepNavigator = new StepNavigator(element);
    this.messages = new Messages(element);
    this.validator = new FormValidator(this.messages);
    this.email = '';
    // Initialize handlers
    this.loginHandler = new LoginHandler(element, this.api, this.stepNavigator, this.messages);
    this.registerHandler = new RegisterHandler(
      element,
      this.api,
      this.stepNavigator,
      this.messages,
    );
    this.passwordResetHandler = new PasswordResetHandler(
      element,
      this.api,
      this.stepNavigator,
      this.messages,
    );
    this.changePasswordHandler = new ChangePasswordHandler(
      element,
      this.api,
      this.stepNavigator,
      this.messages,
    );
    this.emailVerificationHandler = new EmailVerificationHandler(
      element,
      this.api,
      this.stepNavigator,
      this.messages,
    );
    this.ssoHandler = new SSOHandler(
      element,
      this.api,
      this.stepNavigator,
      this.messages,
    );

    this.init();
  }

  init() {
    this.bindEvents();
    this.initRedirect();
    this.checkUrlParams();
    this.checkSSOCallback();
    this.initDateMasks();
    this.trackInitialPageLoad();
  }

  bindEvents() {
    // Email check
    this.form.querySelector(FORM_SELECTORS.emailCheck)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleCheckEmail();
    });

    // Login
    this.form.querySelector(FORM_SELECTORS.login)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleLogin();
    });

    // Registration
    this.form.querySelector(FORM_SELECTORS.register)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleRegister();
    });

    // SSO Registration
    this.form.querySelector(FORM_SELECTORS.registerSso)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSSORegister();
    });

    // Password reset
    this.form.querySelector(FORM_SELECTORS.resetPassword)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleResetPassword();
    });

    // Change password
    this.form.querySelector(FORM_SELECTORS.changePassword)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleChangePassword();
    });

    // Resend verification
    this.form.querySelector(FORM_SELECTORS.resendVerification)?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleResendVerification();
    });

    // "Forgot password?" link
    this.form.querySelectorAll('[data-action="reset-password"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // Track Forgot Password click
        pushFunnelEvent({
          section: 'Sign In',
          step: 3,
          label: 'Sign In - Forgot Password Prompt',
          method: 'Email',
        });
        this.goToResetPassword();
      });
    });

    // Handler for "Forgot password" link in error message
    this.form.addEventListener('click', (e) => {
      const link = e.target.closest('.auth-form__error-link[data-action="reset-password"]');
      if (link) {
        e.preventDefault();
        const errorMessageEl = link.closest('.auth-form__error-message--wrong-password');
        const email = errorMessageEl?.dataset.userEmail;
        if (email) {
          DOMUtils.setFieldValue(FIELD_SELECTORS.resetEmail, email, this.form);
        }

        // Track Forgot Password click from error message
        pushFunnelEvent({
          section: 'Sign In',
          step: 3,
          label: 'Sign In - Forgot Password Prompt',
          method: 'Email',
        });

        this.goToResetPassword();
      }
    });

    // SSO link handler - save state token before redirect
    this.form.querySelectorAll('[data-sso-state]').forEach((link) => {
      link.addEventListener('click', () => {
        const state = link.getAttribute('data-sso-state');
        if (state) {
          sessionStorage.setItem('azds_sso_state', state);
        }

        const redirectUrl = link.dataset.redirect || this.form.dataset.redirectUrl;
        if (!redirectUrl) {
          return;
        }

        localStorage.setItem('azds_sso_redirect', redirectUrl);

        // Track Google SSO click
        pushFunnelEvent({
          section: 'Sign In',
          step: 1,
          label: 'Sign In - Start',
          method: 'Google',
        });
      });
    });
  }

  /**
   * Track initial page load for Sign In funnel
   */
  trackInitialPageLoad() {
    // Only track if on the email step (first step)
    const emailStep = this.form.querySelector('.auth-form__step--email');
    if (emailStep && emailStep.style.display !== 'none') {
      pushFunnelEvent({
        section: 'Sign In',
        step: 1,
        label: 'Sign In - Start',
        method: 'Email',
      });
    }
  }

  /**
   * Initialize date input masks
   */
  initDateMasks() {
    this.dateInputs = DateInput.initAll(this.form, { messages: this.messages });
  }

  /**
   * Check URL parameters (for password reset and verification)
   */
  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get('key');
    const login = urlParams.get('login');
    const action = urlParams.get('action');

    // Email verification
    if (action === 'verify-email' && key && login) {
      this.emailVerificationHandler.validateAndShowVerifyEmailForm(key, login);
      return;
    }

    // Password reset
    if (key && login && (action === 'rp' || !action)) {
      this.changePasswordHandler.validateAndShowChangePasswordForm(key, login);
    }
  }

  /**
   * Initialize redirect URL from query params
   */
  initRedirect() {
    const redirectUrl = AuthForm.getRedirectParam()
      || AuthForm.normalizeRedirect(localStorage.getItem('azds_sso_redirect'));
    if (redirectUrl) {
      this.form.dataset.redirectUrl = redirectUrl;
      localStorage.setItem('azds_sso_redirect', redirectUrl);
    }
  }

  /**
   * Get safe redirect param (same-origin or relative only)
   */
  static getRedirectParam() {
    const urlParams = new URLSearchParams(window.location.search);
    return AuthForm.normalizeRedirect(urlParams.get('redirect'));
  }

  /**
   * Normalize and validate redirect param (same-origin or relative only)
   */
  static normalizeRedirect(redirect) {
    if (!redirect) {
      return null;
    }

    try {
      const resolved = new URL(redirect, window.location.origin);
      if (resolved.origin !== window.location.origin) {
        return null;
      }
      return resolved.pathname + resolved.search + resolved.hash;
    } catch (e) {
      return null;
    }
  }

  /**
   * Handle email check
   */
  async handleCheckEmail() {
    const emailInput = this.form.querySelector(FIELD_SELECTORS.userEmail);
    this.email = emailInput.value.trim();

    if (!FormValidator.validateEmail(this.email)) {
      DOMUtils.showError(emailInput, this.messages.getMessage('error_email_invalid'));
      return;
    }

    const emailCheckForm = this.form.querySelector(FORM_SELECTORS.emailCheck);
    DOMUtils.setLoading(emailCheckForm, true, this.form);

    try {
      const { response, data } = await this.api.checkEmail(this.email);

      if (response.ok) {
        let step;
        if (data.exists) {
          // Check if user is SSO-only
          if (data.sso_only) {
            step = 'sso-login';
          } else {
            step = 'login';
          }
        } else {
          step = 'register';
        }

        this.stepNavigator.goToStep(step);

        if (step === 'login') {
          DOMUtils.setFieldValue(FIELD_SELECTORS.loginEmail, this.email, this.form);

          // Track funnel step 2: Enter Password
          pushFunnelEvent({
            section: 'Sign In',
            step: 2,
            label: 'Sign In - Enter Password',
            method: 'Email',
          });
        } else if (step === 'register') {
          DOMUtils.setFieldValue(FIELD_SELECTORS.regEmail, this.email, this.form);

          // Track Sign Up step 1: Start
          pushFunnelEvent({
            section: 'Sign Up',
            step: 1,
            label: 'Sign Up - Start',
            method: 'Email',
          });
        } else if (step === 'sso-login') {
          DOMUtils.setFieldValue(FIELD_SELECTORS.ssoLoginEmail, this.email, this.form);
        }
      } else {
        const errorMessage = this.messages.getMessageFromApi(data.message, {}) || this.messages.getMessage('error_generic');
        DOMUtils.showError(emailInput, errorMessage);
      }
    } catch (error) {
      const errorMessage = this.messages.getMessageFromApi(error.message, {}) || this.messages.getMessage('error_connection');
      DOMUtils.showError(emailInput, errorMessage);
    } finally {
      DOMUtils.setLoading(emailCheckForm, false, this.form);
    }
  }

  /**
   * Handle login
   */
  handleLogin() {
    const passwordInput = this.form.querySelector(FIELD_SELECTORS.userPass);
    const loginEmailInput = this.form.querySelector(FIELD_SELECTORS.loginEmail);
    const password = passwordInput.value;
    const email = (this.email || loginEmailInput?.value || '').trim();

    if (!FormValidator.validateEmail(email)) {
      if (loginEmailInput) {
        DOMUtils.showError(loginEmailInput, this.messages.getMessage('error_email_invalid'));
      }
      return;
    }

    this.email = email;

    // Track Sign In button click
    pushFunnelEvent({
      section: 'Sign In',
      step: 3,
      label: 'Sign In - Sign In Button',
      method: 'Email',
    });

    this.loginHandler.handleLogin(this.email, password);
  }

  /**
   * Handle registration
   */
  handleRegister() {
    // Track Sign Up step 2: Create Account
    pushFunnelEvent({
      section: 'Sign Up',
      step: 2,
      label: 'Sign Up - Create Account',
      method: 'Email',
    });

    this.registerHandler.handleRegister(this.email, false);
  }

  /**
   * Handle SSO registration
   */
  handleSSORegister() {
    const emailInput = this.form.querySelector(FIELD_SELECTORS.regSsoEmail);
    const email = emailInput ? emailInput.value.trim() : this.email;

    // Track Sign Up step 2: Create Account
    pushFunnelEvent({
      section: 'Sign Up',
      step: 2,
      label: 'Sign Up - Create Account',
      method: 'Google',
    });

    this.registerHandler.handleRegister(email, true);
  }

  /**
   * Handle password reset request
   */
  handleResetPassword() {
    const emailInput = this.form.querySelector(FIELD_SELECTORS.resetEmail);
    const email = emailInput.value.trim();
    this.passwordResetHandler.handleResetPassword(email);
  }

  /**
   * Handle password change
   */
  handleChangePassword() {
    this.changePasswordHandler.handleChangePassword();
  }

  /**
   * Handle resend verification
   */
  handleResendVerification() {
    // Track Sign Up step 3: Resend Link
    pushFunnelEvent({
      section: 'Sign Up',
      step: 3,
      label: 'Sign Up - Resend Link',
      method: 'Email',
    });

    this.emailVerificationHandler.handleResendVerification();
  }

  /**
   * Navigate to password reset step
   */
  goToResetPassword() {
    const resetEmailInput = this.form.querySelector(FIELD_SELECTORS.resetEmail);

    // Set email from current step if available
    if (this.email && resetEmailInput) {
      resetEmailInput.value = this.email;
    } else {
      // Try to get email from SSO login step
      const ssoLoginEmailInput = this.form.querySelector(FIELD_SELECTORS.ssoLoginEmail);
      if (ssoLoginEmailInput && ssoLoginEmailInput.value && resetEmailInput) {
        resetEmailInput.value = ssoLoginEmailInput.value;
      }
    }

    this.stepNavigator.goToStep('reset-password');
    this.passwordResetHandler.initResetPasswordEmail();

    // Track Forgot Password page load
    pushFunnelEvent({
      section: 'Forgot Password',
      step: 1,
      label: 'Forgot Password - Start',
      method: 'Email',
    });
  }

  /**
   * Check for SSO callback in URL
   */
  checkSSOCallback() {
    this.ssoHandler.handleSSOCallback();
  }
}

// Export for use
export default AuthForm;

// Initialize on DOM load
const initAuthForm = () => {
  const authForm = document.querySelector('.auth-form');
  if (authForm) {
    // eslint-disable-next-line no-new
    new AuthForm(authForm);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAuthForm);
} else {
  initAuthForm();
}
